
import React, { useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation';
import { useProject } from '@/Hooks/UseProject';
import { getExcelFirstSheetData, getExcelSecondSheetData } from '@/Helpers/NewDashboardHelper';

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentBody from '@/Layouts/MainLayout/components/ContentBody';

import ContentBodyHeader from '@/Components/ResourcesCncPageComponents/ContentBodyHeader/ContentBodyHeader';
import EstimatedDeviation from './Components/EstimatedDeviation';
import ModalExporter from '@/Components/ModalExporter/ModalExporter';

import ProjectProgress from './Components/ProjectProgress';
import ProjectSCurve from './Components/ProjectSCurve';
import TasksStatus from './Components/TasksStatus';
import Skeleton from './Components/Skeleton';

import './NewDashboardPage.scss'

const NewDashboardPage = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { projectLoaded, project, nonWorkingDays } = useProject()

  const { programmationLoaded } = useProgrammation()

  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const dashboard = useSelector((state: any) => state.DashboardReducer)

  const [ showExportModal, setShowExportModal ] = useState(false)
  const [ exportData, setExportData ] = useState<any | null>(null)
  const [ projectProgress, setProjectProgress ] = useState<any | null>(null)
  const [ estimatedEndDate, setEstimatedEndDate ] = useState('')
  const [ performance, setPerformance ] = useState(0)
  const [ deviation, setDeviation ] = useState(0)

  const projectStatus = useMemo(() => {

    if (!project) return null

    const isStarted = moment(project.startsAt).isBefore(moment())

    if (!isStarted) return 'pending-start'

    const isClosed = moment(project.endsAt).isBefore(moment())

    const isOnTime = isStarted && !isClosed && deviation === 0
    const isAdvanced = isStarted && !isClosed && deviation > 0
    const isOverdue = isStarted && !isClosed && deviation < 0

    if (isOnTime) return 'on-time'
    if (isAdvanced) return 'advanced'
    if (isOverdue) return 'overdue'

    if (isClosed) {

      const uncompletedTasks = programmation.tasks.filter((task: any) => task.progress < 100)

      if (uncompletedTasks.length) return 'with-uncompleted-closure'

      return 'closed'
    }
    return null
  }, [project, deviation, programmation.tasks])

  useEffect(() => {

    if (!projectLoaded || !programmationLoaded) return

    const { tasks, list, users, colorCodes } = programmation

    const excelFirstSheetData = getExcelFirstSheetData(tasks, list, users, colorCodes, nonWorkingDays, t)

    const {
      weeks: excelSecondSheetData,
      progress,
      estimatedEndDate,
      performance,
      deviation
    } = getExcelSecondSheetData(project, tasks, nonWorkingDays, t)

    setProjectProgress(progress)
    setEstimatedEndDate(estimatedEndDate)
    setPerformance(performance)
    setDeviation(deviation)

    const data = {
      firstSheet: excelFirstSheetData,
      secondSheet: excelSecondSheetData
    }

    setExportData(data)
  }, [projectLoaded, programmationLoaded])

  const handleExport = async () => {

    setShowExportModal(true)
  }

  return (
    <ProjectLayout menuName="home" subMenuName="dashboard">
      <ContentBody noScroll>
        <ContentBodyHeader
          title={t('PROJECT_DASHBOARD:PageTitle')}
          buttons={[
            {
              icon: <i className="fas fa-download"></i>,
              label: t('PROJECT_DASHBOARD:ExportButton'),
              onClick: handleExport,
              visible: true,
              active: programmationLoaded
            }
          ]}
        />
        <Row className="page-dashboard">
          <Col>
            <Row>
              <Col className='mb-4'>
                {!projectProgress && (
                  <Skeleton width={'100%'} height={320} borderRadius={10} count={1} />
                )}
                {projectProgress && (
                  <EstimatedDeviation
                    progress={(projectProgress.actual && projectProgress.projectMax) ? Math.round(projectProgress.actual / projectProgress.projectMax * 100) : 0}
                    deviation={deviation}
                    estimatedEndDate={estimatedEndDate}
                    performance={performance.toString()}
                    projectStatus={projectStatus}
                  />
                )}
              </Col>
              <Col className='mb-4'>
                {!projectProgress && (
                  <Skeleton width={'100%'} height={320} borderRadius={10} count={1} />
                )}
                {projectProgress && (
                  <ProjectProgress
                    projectProgress={projectProgress}
                  />
                )}
              </Col>
              <Col className='mb-4'>
                <TasksStatus />
              </Col>
            </Row>
            <Row>
              <Col>
                {!exportData && (
                  <Skeleton width={'100%'} height={420} borderRadius={10} count={1} />
                )}
                {exportData && (
                  <ProjectSCurve data={exportData?.secondSheet} />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </ContentBody>

      <ModalExporter
        show={showExportModal}
        title={t('MODAL_EXPORTER:DashboardExcelTitle')}
        file="dashboard-excel"
        exportData={exportData}
        onHide={() => setShowExportModal(false)}
      />
    </ProjectLayout>
  )
}

export default NewDashboardPage
